import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { MessageSquare, Package } from 'lucide-react';

import { userReviewsRoute } from '../../models/routes';
import { cn } from '../../utils';

interface Props {
    currentUrl: string;
    sellerNickname: string;
    sellerUrl: string;
    embedded?: boolean;
    showReviews?: boolean;
}

export const SellerPrimaryNavigation: FC<Props> = ({
    currentUrl,
    sellerNickname,
    sellerUrl,
    embedded = false,
    showReviews = true,
}) => {
    const reviewsUrl = userReviewsRoute(sellerNickname);
    const isReviewsActive = showReviews && currentUrl.startsWith(reviewsUrl);

    const items = [
        {
            href: sellerUrl,
            label: 'Товари',
            icon: Package,
            active: !showReviews || !isReviewsActive,
            testId: 'seller-primary-products',
        },
        ...(showReviews
            ? [
                  {
                      href: reviewsUrl,
                      label: 'Відгуки',
                      icon: MessageSquare,
                      active: isReviewsActive,
                      testId: 'seller-primary-reviews',
                  },
              ]
            : []),
    ];

    const nav = (
        <nav
            className={cn(
                embedded
                    ? cn(
                          'grid w-full gap-2 rounded-2xl bg-muted/60 p-1.5 sm:inline-grid sm:w-auto sm:grid-flow-col',
                          items.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
                      )
                    : cn(
                          'grid gap-2 rounded-2xl border border-border/70 bg-muted/40 p-2 shadow-sm',
                          items.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
                      ),
            )}
            aria-label="Навігація сторінки продавця"
        >
            {items.map((item) => {
                const Icon = item.icon;

                return (
                    <Link
                        key={item.href}
                        href={item.href}
                        aria-current={item.active ? 'page' : undefined}
                        data-testid={item.testId}
                        className={cn(
                            'inline-flex items-center justify-center gap-2 rounded-xl border text-center text-sm font-semibold transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
                            embedded
                                ? 'min-h-10 w-full px-3 whitespace-normal sm:px-4 sm:whitespace-nowrap'
                                : 'min-h-11 w-full px-4 whitespace-nowrap',
                            item.active
                                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                                : 'border-border/80 bg-background text-foreground hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm',
                        )}
                    >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                    </Link>
                );
            })}
        </nav>
    );

    if (embedded) {
        return <div data-testid="seller-primary-nav">{nav}</div>;
    }

    return (
        <section className="pb-2" data-testid="seller-primary-nav">
            <div className="container px-4 sm:px-6 lg:px-8">{nav}</div>
        </section>
    );
};
